import React, { useState } from 'react';
import { X } from 'lucide-react';

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: Record<string, string>;
  onApply: (filters: Record<string, string>) => void;
  onClear: () => void;
}

const STATUS_OPTIONS = [
  { value: '',            label: 'Todos os Status' },
  { value: 'RECEBIDA',    label: 'Recebida' },
  { value: 'EM_TRIAGEM',  label: 'Em Triagem' },
  { value: 'EM_ANALISE',  label: 'Em Análise' },
  { value: 'APROVADA',    label: 'Aprovada' },
  { value: 'CONCLUIDA',   label: 'Concluída' },
  { value: 'ARQUIVADA',   label: 'Arquivada' },
];

const PRIORITY_OPTIONS = [
  { value: '',       label: 'Todas as Prioridades' },
  { value: 'URGENTE',label: '🔴 Urgente' },
  { value: 'ALTA',   label: '🟠 Alta' },
  { value: 'MEDIA',  label: '🔵 Média' },
  { value: 'BAIXA',  label: '🟢 Baixa' },
];

const CABINET_OPTIONS = [
  { value: '',       label: 'Todos os Gabinetes' },
  { value: 'STATE',  label: 'Estadual (Dani Alonso)' },
  { value: 'FEDERAL',label: 'Federal (Cap. Augusto)' },
  { value: 'BOTH',   label: 'Ambos' },
];

const SOURCE_OPTIONS = [
  { value: '',         label: 'Todas as Origens' },
  { value: 'WHATSAPP', label: '💬 WhatsApp' },
  { value: 'EMAIL',    label: '✉️ E-mail' },
  { value: 'PDF',      label: '📄 PDF' },
  { value: 'IMAGE',    label: '🖼️ Imagem' },
  { value: 'MANUAL',   label: '✏️ Manual' },
];

const THEME_OPTIONS = [
  { value: '',                  label: 'Todas as Áreas' },
  { value: 'Saúde',             label: '🏥 Saúde' },
  { value: 'Educação',          label: '📚 Educação' },
  { value: 'Infraestrutura',    label: '🏗️ Infraestrutura' },
  { value: 'Segurança Pública', label: '🚔 Segurança Pública' },
  { value: 'Assistência Social',label: '🤝 Assistência Social' },
  { value: 'Habitação',         label: '🏠 Habitação' },
  { value: 'Cultura',           label: '🎭 Cultura' },
  { value: 'Esporte',           label: '⚽ Esporte' },
  { value: 'Transporte',        label: '🚌 Transporte' },
];

interface SelectGroupProps {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}

const SelectGroup: React.FC<SelectGroupProps> = ({ label, options, value, onChange }) => (
  <div>
    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">
      {label}
    </label>
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
            value === opt.value
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onApply,
  onClear,
}) => {
  const [local, setLocal] = useState<Record<string, string>>(filters);

  const set = (key: string, value: string) => setLocal(prev => ({ ...prev, [key]: value }));

  const activeCount = Object.values(local).filter(v => v !== '').length;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="md:hidden fixed inset-0 bg-black/40 z-40 fade-backdrop"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="md:hidden fixed bottom-[60px] left-0 right-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl slide-up max-h-[85vh] flex flex-col">
        {/* Handle + Header */}
        <div className="px-5 pt-4 pb-0">
          <div className="w-10 h-1 bg-slate-200 dark:bg-slate-700 rounded-full mx-auto mb-4" />
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-base">Filtros</h3>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <span className="text-[10px] font-bold bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-300 px-2 py-0.5 rounded-full">
                  {activeCount} ativo{activeCount > 1 ? 's' : ''}
                </span>
              )}
              <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Filter groups — scrollable */}
        <div className="overflow-y-auto px-5 pb-4 space-y-5 flex-1">
          <SelectGroup label="Status"        options={STATUS_OPTIONS}   value={local.status    ?? ''} onChange={v => set('status', v)} />
          <SelectGroup label="Prioridade"    options={PRIORITY_OPTIONS} value={local.priority  ?? ''} onChange={v => set('priority', v)} />
          <SelectGroup label="Gabinete"      options={CABINET_OPTIONS}  value={local.cabinet   ?? ''} onChange={v => set('cabinet', v)} />
          <SelectGroup label="Origem"        options={SOURCE_OPTIONS}   value={local.source    ?? ''} onChange={v => set('source', v)} />
          <SelectGroup label="Área Temática" options={THEME_OPTIONS}    value={local.themeArea ?? ''} onChange={v => set('themeArea', v)} />
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800 flex gap-3 safe-bottom">
          <button
            type="button"
            onClick={() => { setLocal({}); onClear(); }}
            className="flex-1 py-3 rounded-2xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={() => { onApply(local); onClose(); }}
            className="flex-1 py-3 rounded-2xl bg-blue-600 text-white text-sm font-bold shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-500"
          >
            Aplicar Filtros
          </button>
        </div>
      </div>
    </>
  );
};
